import type dxDataGrid from 'devextreme/ui/data_grid';
import type { StorageMountCatalog } from './fileBrowserApi';

export function fileBrowserGridStateKey(catalog: StorageMountCatalog = 'all'): string {
  return `jarvis-file-browser-grid-${catalog}`;
}

export const FILE_BROWSER_DETAILS_STATE_KEY = fileBrowserGridStateKey('all');

const LEGACY_FILE_BROWSER_DETAILS_STATE_KEYS = [
  'jarvis.fileBrowser.detailsGrid.v4',
  'jarvis.fileBrowser.detailsGrid.v3',
  'jarvis.fileBrowser.detailsGrid.v2',
  'jarvis.fileBrowser.detailsGrid.v1'
];

const REQUIRED_VISIBLE_COLUMN = 'fileItem.name';
const INTERNAL_HIDDEN_COLUMN = 'fileItem.isParentFolder';

export type FileBrowserChooserColumn = {
  dataField: string;
  fallbackCaption: string;
  required?: boolean;
};

export const FILE_BROWSER_CHOOSER_COLUMNS: readonly FileBrowserChooserColumn[] = [
  { dataField: 'fileItem.thumbnail', fallbackCaption: 'Preview' },
  { dataField: 'fileItem.name', fallbackCaption: 'Name', required: true },
  { dataField: 'fileItem.dataItem.actions', fallbackCaption: 'Actions' },
  { dataField: 'fileItem.dataItem.extension', fallbackCaption: 'Type' },
  { dataField: 'fileItem.dateModified', fallbackCaption: 'Modified' },
  { dataField: 'fileItem.size', fallbackCaption: 'Size' }
];

const PERSISTED_COLUMN_FIELDS = [
  ...FILE_BROWSER_CHOOSER_COLUMNS.map((column) => column.dataField),
  INTERNAL_HIDDEN_COLUMN
];

type SavedColumnState = {
  dataField?: string;
  name?: string;
  visible?: boolean;
  visibleIndex?: number;
  width?: number | string;
};

const layoutWatchGrids = new WeakSet<dxDataGrid>();
const layoutSaveTimers = new WeakMap<dxDataGrid, ReturnType<typeof setTimeout>>();
const gridCatalogByInstance = new WeakMap<dxDataGrid, StorageMountCatalog>();
let layoutApplyDepth = 0;

export function isApplyingFileBrowserGridLayout(): boolean {
  return layoutApplyDepth > 0;
}

function withLayoutApply<T>(action: () => T): T {
  layoutApplyDepth += 1;
  try {
    return action();
  } finally {
    layoutApplyDepth -= 1;
  }
}

function getColumnOrderIndexOffset(grid: dxDataGrid): number {
  let offset = 0;
  const count = grid.columnCount();
  for (let index = 0; index < count; index += 1) {
    const type = grid.columnOption(index, 'type');
    if (type === 'selection' || type === 'buttons' || type === 'adaptive') {
      offset += 1;
    }
  }
  return offset;
}

function scheduleLayoutSave(grid: dxDataGrid): void {
  if (layoutApplyDepth > 0) return;

  const existing = layoutSaveTimers.get(grid);
  if (existing) clearTimeout(existing);
  layoutSaveTimers.set(
    grid,
    setTimeout(() => {
      layoutSaveTimers.delete(grid);
      if (layoutApplyDepth > 0) return;
      saveFileBrowserGridStateFromGrid(grid, gridCatalogByInstance.get(grid) ?? 'all');
    }, 150)
  );
}

export function attachFileBrowserGridLayoutWatch(
  grid: dxDataGrid,
  catalog: StorageMountCatalog = 'all'
): void {
  gridCatalogByInstance.set(grid, catalog);
  if (layoutWatchGrids.has(grid)) return;
  layoutWatchGrids.add(grid);

  grid.on('optionChanged', (event: { name?: string; fullName?: string }) => {
    if (event.name === 'dataSource') {
      requestAnimationFrame(() => reapplyFileBrowserGridLayout(grid));
      return;
    }

    if (event.name === 'columns' || event.fullName?.startsWith('columns')) {
      scheduleLayoutSave(grid);
    }
  });
}

function sanitizeSavedColumns(columns: SavedColumnState[]): SavedColumnState[] {
  const next = columns.map((column) => ({ ...column }));

  for (const column of next) {
    const dataField = column.dataField ?? '';
    if (dataField === REQUIRED_VISIBLE_COLUMN) {
      column.visible = true;
    }
    if (dataField === INTERNAL_HIDDEN_COLUMN) {
      column.visible = false;
    }
  }

  const visibleDataColumns = next.filter(
    (column) => column.visible !== false && column.dataField !== INTERNAL_HIDDEN_COLUMN
  );
  if (visibleDataColumns.length === 0) {
    for (const column of next) {
      if (column.dataField === REQUIRED_VISIBLE_COLUMN) {
        column.visible = true;
      }
    }
  }

  return next;
}

function normalizeColumnOrder(columnOrder: unknown): string[] {
  if (!Array.isArray(columnOrder)) return [];

  const allowed = new Set(PERSISTED_COLUMN_FIELDS);
  const seen = new Set<string>();
  const next: string[] = [];

  for (const entry of columnOrder) {
    if (typeof entry !== 'string' || !allowed.has(entry) || seen.has(entry)) continue;
    seen.add(entry);
    next.push(entry);
  }

  for (const dataField of PERSISTED_COLUMN_FIELDS) {
    if (!seen.has(dataField)) next.push(dataField);
  }

  return next;
}

export function sanitizeFileBrowserGridState(
  state: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!state) return null;

  const next = { ...state };
  if (Array.isArray(state.columns)) {
    next.columns = sanitizeSavedColumns(state.columns as SavedColumnState[]);
  }
  if (Array.isArray(state.columnOrder)) {
    next.columnOrder = normalizeColumnOrder(state.columnOrder);
  }
  return next;
}

export function readFileBrowserGridState(catalog: StorageMountCatalog = 'all'): Record<string, unknown> | null {
  const primaryKey = fileBrowserGridStateKey(catalog);
  const keys = [primaryKey, ...LEGACY_FILE_BROWSER_DETAILS_STATE_KEYS];
  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (!parsed || typeof parsed !== 'object') continue;

      const sanitized = sanitizeFileBrowserGridState(parsed);
      if (!sanitized) continue;

      if (key !== primaryKey) {
        writeFileBrowserGridState(sanitized, catalog);
      }
      return sanitized;
    } catch {
      // try next key
    }
  }
  return null;
}

export function writeFileBrowserGridState(
  state: Record<string, unknown>,
  catalog: StorageMountCatalog = 'all'
): void {
  try {
    const sanitized = sanitizeFileBrowserGridState(state) ?? state;
    localStorage.setItem(fileBrowserGridStateKey(catalog), JSON.stringify(sanitized));
  } catch {
    // ignore quota / private mode errors
  }
}

export function getColumnOrderFromGrid(grid: dxDataGrid): string[] {
  return getChooserColumnsInDisplayOrder(grid).map((column) => column.dataField);
}

export function applyColumnOrder(grid: dxDataGrid, columnOrder: string[]): void {
  const normalized = normalizeColumnOrder(columnOrder);
  if (normalized.length === 0) return;

  const offset = getColumnOrderIndexOffset(grid);

  grid.beginUpdate();
  try {
    normalized.forEach((dataField, index) => {
      grid.columnOption(dataField, 'visibleIndex', offset + index);
    });
  } finally {
    grid.endUpdate();
  }
}

export function captureFileBrowserGridState(grid: dxDataGrid): Record<string, unknown> {
  const base = { ...((grid.state() ?? {}) as Record<string, unknown>) };
  const columnOrder = getColumnOrderFromGrid(grid);
  const columns = PERSISTED_COLUMN_FIELDS.map((dataField) => ({
    dataField,
    visible:
      dataField === INTERNAL_HIDDEN_COLUMN
        ? false
        : grid.columnOption(dataField, 'visible') !== false,
    visibleIndex: Number(grid.columnOption(dataField, 'visibleIndex') ?? 0),
    width: grid.columnOption(dataField, 'width') as number | string | undefined
  }));

  return (
    sanitizeFileBrowserGridState({
      ...base,
      columnOrder,
      columns
    }) ?? { ...base, columnOrder, columns }
  );
}

export function saveFileBrowserGridStateFromGrid(
  grid: dxDataGrid,
  catalog: StorageMountCatalog = 'all'
): void {
  writeFileBrowserGridState(captureFileBrowserGridState(grid), catalog);
}

export function enforceMandatoryFileBrowserGridColumns(grid: dxDataGrid): void {
  grid.columnOption(REQUIRED_VISIBLE_COLUMN, {
    visible: true,
    allowHiding: false,
    minWidth: 120,
    hidingPriority: 100
  });
  grid.columnOption(INTERNAL_HIDDEN_COLUMN, {
    visible: false,
    showInColumnChooser: false,
    allowHiding: false
  });
  grid.option('showColumnHeaders', true);
}

export function applySavedColumnLayout(
  grid: dxDataGrid,
  state: Record<string, unknown> | null | undefined,
  _catalog: StorageMountCatalog = 'all'
): void {
  if (!state) return;

  const columns = Array.isArray(state.columns)
    ? sanitizeSavedColumns(state.columns as SavedColumnState[])
    : [];

  withLayoutApply(() => {
    grid.beginUpdate();
    try {
      const columnOrder = normalizeColumnOrder(state.columnOrder);
      if (columnOrder.length > 0) {
        applyColumnOrder(grid, columnOrder);
      } else if (columns.length > 0) {
        for (const column of columns) {
          if (!column.dataField || column.visibleIndex === undefined) continue;
          grid.columnOption(column.dataField, 'visibleIndex', column.visibleIndex);
        }
      }

      for (const column of columns) {
        if (!column.dataField) continue;
        if (column.width !== undefined) {
          grid.columnOption(column.dataField, 'width', column.width);
        }
        if (column.visible !== undefined) {
          grid.columnOption(column.dataField, 'visible', column.visible);
        }
      }

      enforceMandatoryFileBrowserGridColumns(grid);
    } finally {
      grid.endUpdate();
    }
  });
}

export function reapplyFileBrowserGridLayout(
  grid: dxDataGrid,
  catalog: StorageMountCatalog = 'all'
): void {
  const saved = readFileBrowserGridState(catalog);
  if (!saved) return;
  applySavedColumnLayout(grid, saved, catalog);
}

export function applyFileBrowserGridStateStoring(
  grid: dxDataGrid,
  catalog: StorageMountCatalog = 'all'
): void {
  gridCatalogByInstance.set(grid, catalog);
  grid.option('stateStoring', {
    enabled: true,
    type: 'custom',
    savingTimeout: 500,
    customLoad: () => Promise.resolve(readFileBrowserGridState(catalog)),
    customSave: () => {
      if (isApplyingFileBrowserGridLayout()) return;
      saveFileBrowserGridStateFromGrid(grid, catalog);
    }
  });
}

export function chooserColumnCaption(grid: dxDataGrid, column: FileBrowserChooserColumn): string {
  const caption = grid.columnOption(column.dataField, 'caption');
  if (typeof caption === 'string' && caption.trim()) return caption.trim();
  return column.fallbackCaption;
}

export function chooserColumnIsVisible(grid: dxDataGrid, column: FileBrowserChooserColumn): boolean {
  if (column.required) return true;
  return grid.columnOption(column.dataField, 'visible') !== false;
}

export function setChooserColumnVisible(
  grid: dxDataGrid,
  dataField: string,
  visible: boolean,
  catalog: StorageMountCatalog = 'all'
): void {
  grid.columnOption(dataField, 'visible', visible);
  enforceMandatoryFileBrowserGridColumns(grid);
  saveFileBrowserGridStateFromGrid(grid, catalog);
}

export type ChooserColumnRow = {
  dataField: string;
  caption: string;
  required: boolean;
  visible: boolean;
  visibleIndex: number;
};

export function getChooserColumnsInDisplayOrder(grid: dxDataGrid): ChooserColumnRow[] {
  return FILE_BROWSER_CHOOSER_COLUMNS.map((column) => ({
    dataField: column.dataField,
    caption: chooserColumnCaption(grid, column),
    required: !!column.required,
    visible: chooserColumnIsVisible(grid, column),
    visibleIndex: Number(grid.columnOption(column.dataField, 'visibleIndex') ?? 0)
  })).sort((left, right) => left.visibleIndex - right.visibleIndex);
}

export function moveChooserColumn(
  grid: dxDataGrid,
  dataField: string,
  direction: -1 | 1,
  catalog: StorageMountCatalog = 'all'
): void {
  const ordered = getChooserColumnsInDisplayOrder(grid);
  const visibleFields = ordered.filter((column) => column.visible).map((column) => column.dataField);
  const index = visibleFields.indexOf(dataField);
  if (index < 0) return;

  const target = index + direction;
  if (target < 0 || target >= visibleFields.length) return;

  const nextVisible = [...visibleFields];
  [nextVisible[index], nextVisible[target]] = [nextVisible[target], nextVisible[index]];

  const hiddenFields = ordered.filter((column) => !column.visible).map((column) => column.dataField);
  applyColumnOrder(grid, [...nextVisible, ...hiddenFields]);
  enforceMandatoryFileBrowserGridColumns(grid);
  saveFileBrowserGridStateFromGrid(grid, catalog);
}
